import { FormikErrors, useFormikContext } from 'formik';
import { useEffect } from 'react';
import toast from 'src/utils/toast';

type AnyObj = Record<string, any>;

function findFirstErrorKey(
  errors: FormikErrors<AnyObj>,
  prefix = ''
): string | null {
  for (const key of Object.keys(errors)) {
    const value = (errors as AnyObj)[key];
    const path = prefix ? `${prefix}.${key}` : key;

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const deeper = findFirstErrorKey(value, path);
      if (deeper) return deeper;
    } else if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        const item = value[i];
        if (item && typeof item === 'object') {
          const deeper = findFirstErrorKey(item, `${path}.${i}`);
          if (deeper) return deeper;
        } else if (item) {
          return `${path}.${i}`;
        }
      }
    } else {
      return path;
    }
  }
  return null;
}

function extractErrorMessages(errors: FormikErrors<AnyObj>): string[] {
  const messages: string[] = [];

  for (const key of Object.keys(errors)) {
    const value = (errors as AnyObj)[key];

    if (typeof value === 'string') {
      messages.push(value);
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Recursively extract messages from nested objects
      const nestedMessages = extractErrorMessages(value);
      messages.push(...nestedMessages);
    } else if (Array.isArray(value)) {
      // Handle array errors
      for (const item of value) {
        if (typeof item === 'string') {
          messages.push(item);
        } else if (item && typeof item === 'object') {
          const nestedMessages = extractErrorMessages(item);
          messages.push(...nestedMessages);
        }
      }
    }
  }

  return messages;
}

export default function ScrollToFirstError<T>({
  offset = 80,
  toastErrors = false,
  scrollTo = true,
}) {
  const { isSubmitting, errors } = useFormikContext<T>();
  useEffect(() => {
    if (isSubmitting && Object.keys(errors).length > 0) {
      const firstErrorKey = findFirstErrorKey(errors);
      if (!firstErrorKey) return;
      if (toastErrors) {
        const errorMessages = extractErrorMessages(errors);
        if (errorMessages.length > 0) {
          toast.error(errorMessages.join('\n'));
        }
      }

      const errorElement = document.querySelector<HTMLElement>(
        `[name="${firstErrorKey}"]`
      );

      if (errorElement) {
        const rect = errorElement.getBoundingClientRect();
        const absoluteElementTop = window.scrollY + rect.top;
        const timer = setTimeout(() => {
          if (scrollTo) {
            window.scrollTo({
              top: absoluteElementTop - offset,
              behavior: 'smooth',
            });
          }
          try {
            errorElement.focus?.({ preventScroll: true } as any);
          } catch {
            errorElement.focus?.();
          }
          clearTimeout(timer);
        }, 150);
      }
    }
  }, [errors, isSubmitting, offset, toastErrors, scrollTo]);

  return null;
}
